/**
 * ed50Detector.ts
 * 
 * Detector de referencias al datum ED50 en documentos PTEL.
 * 
 * Contexto: El Real Decreto 1071/2007 estableció ETRS89 como datum oficial
 * en España, pero documentos anteriores a 2007-2008 pueden usar ED50.
 * La diferencia aproximada en Andalucía es de ~100-200m, lo cual es crítico
 * para coordenadas de infraestructuras de emergencia.
 * 
 * Este módulo detecta indicios de ED50 y advierte al usuario, sin realizar
 * transformación automática (requiere confirmación manual del datum origen).
 * 
 * @module ed50Detector
 * @author PTEL Team
 * @since v0.5.3
 */

// =============================================================================
// TIPOS E INTERFACES
// =============================================================================

/** Niveles de confianza en la detección */
export type NivelConfianzaED50 = 'ALTA' | 'MEDIA' | 'BAJA';

/** Tipos de indicador encontrado */
export type TipoIndicadorED50 = 
  | 'REFERENCIA_EXPLICITA'   // ED50, ED-50, ED 50
  | 'CODIGO_EPSG'            // 23029, 23030, 23031
  | 'FECHA_DOCUMENTO'        // Documento pre-2007
  | 'METADATO_APLICACION';   // Software antiguo

/** Indicador individual de posible ED50 */
export interface IndicadorED50 {
  tipo: TipoIndicadorED50;
  valor: string;
  contexto?: string;
  linea?: number;
  confianza: NivelConfianzaED50;
}

/** Resultado completo de la detección */
export interface ResultadoDeteccionED50 {
  posibleED50: boolean;
  confianza: NivelConfianzaED50;
  indicadoresEncontrados: IndicadorED50[];
  warning: string | null;
  recomendacion: string | null;
}

// =============================================================================
// CONSTANTES
// =============================================================================

/**
 * Diferencia aproximada ED50 → ETRS89 en Andalucía (metros)
 * Estos valores son aproximados y varían según la zona.
 * NO usar para transformación automática, solo como referencia.
 */
export const DIFERENCIA_ED50_ETRS89 = {
  deltaX: -102,  // ED50.X - ETRS89.X ≈ -102m
  deltaY: -203,  // ED50.Y - ETRS89.Y ≈ -203m
} as const;

/** Año de transición oficial a ETRS89 en España */
export const AÑO_TRANSICION_ETRS89 = 2007;

/** Códigos EPSG asociados a ED50 */
export const EPSG_ED50 = {
  UTM29: 23029,
  UTM30: 23030,
  UTM31: 23031,
} as const;

/** Códigos EPSG asociados a ETRS89 */
export const EPSG_ETRS89 = {
  UTM29: 25829,
  UTM30: 25830,
  UTM31: 25831,
} as const;

// =============================================================================
// PATRONES DE DETECCIÓN
// =============================================================================

/** Patrones regex para referencias explícitas a ED50 */
const PATRONES_ED50_EXPLICITO = [
  /\bED[\s-]?50\b/gi,                    // ED50, ED-50, ED 50
  /\bEuropean\s*Datum\s*1950\b/gi,       // European Datum 1950
  /\bDatum\s*Europeo\s*1950\b/gi,        // Datum Europeo 1950
  /\bHayford\b/gi,                        // Elipsoide Hayford (asociado a ED50)
  /\bInternacional\s*1924\b/gi,          // Elipsoide Internacional 1924
] as const;

/** Patrones regex para códigos EPSG de ED50 */
const PATRONES_EPSG_ED50 = [
  /\bEPSG[\s:]*23029\b/gi,
  /\bEPSG[\s:]*23030\b/gi,
  /\bEPSG[\s:]*23031\b/gi,
  /\b23029\b/g,  // Solo el código (contexto necesario)
  /\b23030\b/g,
  /\b23031\b/g,
] as const;

/** Aplicaciones/software que típicamente usaban ED50 */
const SOFTWARE_ED50_LEGACY = [
  /\bAutoCAD\s*(200[0-6]|199\d)\b/gi,
  /\bMicroStation\s*(V[78]|SE|J)\b/gi,
  /\bArcView\s*3\b/gi,
  /\bArcGIS\s*(8|9\.0|9\.1)\b/gi,
] as const;

// =============================================================================
// FUNCIONES AUXILIARES
// =============================================================================

/**
 * Extrae contexto alrededor de una coincidencia
 */
function extraerContexto(texto: string, indice: number, radio: number = 50): string {
  const inicio = Math.max(0, indice - radio);
  const fin = Math.min(texto.length, indice + radio);
  let contexto = texto.slice(inicio, fin);
  
  if (inicio > 0) contexto = '...' + contexto;
  if (fin < texto.length) contexto = contexto + '...';
  
  return contexto.replace(/\s+/g, ' ').trim();
}

/**
 * Calcula número de línea aproximado
 */
function calcularLinea(texto: string, indice: number): number {
  return texto.slice(0, indice).split('\n').length;
}

/**
 * Determina confianza global basada en indicadores
 */
function calcularConfianzaGlobal(indicadores: IndicadorED50[]): NivelConfianzaED50 {
  if (indicadores.length === 0) return 'BAJA';
  
  // Si hay referencia explícita = ALTA
  const tieneExplicita = indicadores.some(i => i.tipo === 'REFERENCIA_EXPLICITA');
  if (tieneExplicita) return 'ALTA';
  
  // Si hay código EPSG = ALTA
  const tieneEPSG = indicadores.some(i => i.tipo === 'CODIGO_EPSG');
  if (tieneEPSG) return 'ALTA';
  
  // Múltiples indicadores = usar la confianza más alta entre ellos
  if (indicadores.length >= 2) {
    if (indicadores.some(i => i.confianza === 'ALTA')) return 'ALTA';
    if (indicadores.some(i => i.confianza === 'MEDIA')) return 'MEDIA';
    return 'BAJA';
  }
  
  // Solo un indicador = usar su confianza individual
  return indicadores[0].confianza;
}

/**
 * Genera mensaje de warning según indicadores
 */
function generarWarning(indicadores: IndicadorED50[], confianza: NivelConfianzaED50): string {
  if (indicadores.length === 0) return '';
  
  const tipos = [...new Set(indicadores.map(i => i.tipo))];
  const partes: string[] = [];
  
  if (tipos.includes('REFERENCIA_EXPLICITA')) {
    partes.push('referencias explícitas a ED50');
  }
  if (tipos.includes('CODIGO_EPSG')) {
    const codigos = indicadores
      .filter(i => i.tipo === 'CODIGO_EPSG')
      .map(i => i.valor);
    partes.push(`códigos EPSG ED50 (${codigos.join(', ')})`);
  }
  if (tipos.includes('FECHA_DOCUMENTO')) {
    partes.push('fecha anterior a 2007');
  }
  if (tipos.includes('METADATO_APLICACION')) {
    partes.push('software que usaba ED50 por defecto');
  }
  
  const confianzaTexto = confianza === 'ALTA' ? 'alta probabilidad' :
                         confianza === 'MEDIA' ? 'probabilidad media' : 'posibilidad';
  
  return `⚠️ Detectada ${confianzaTexto} de datum ED50: ${partes.join(', ')}. ` +
         `Las coordenadas podrían tener un desfase de ~100-200m respecto a ETRS89.`;
}

/**
 * Genera recomendación según el nivel de confianza
 */
function generarRecomendacion(confianza: NivelConfianzaED50): string {
  switch (confianza) {
    case 'ALTA':
      return 'Se recomienda verificar el datum con el organismo emisor del documento ' +
             'antes de usar estas coordenadas. Si se confirma ED50, usar herramientas ' +
             'oficiales (IGN) para la transformación a ETRS89.';
    case 'MEDIA':
      return 'Verifique si el documento original especifica el sistema de referencia. ' +
             'En caso de duda, consulte con el departamento técnico municipal.';
    case 'BAJA':
      return 'Aunque la probabilidad es baja, si las coordenadas parecen desplazadas ' +
             '~100-200m, considere que podrían estar en ED50.';
  }
}

// =============================================================================
// FUNCIONES DE DETECCIÓN
// =============================================================================

/**
 * Pre-filtro rápido para determinar si vale la pena análisis profundo.
 * Útil para optimizar el procesamiento de grandes volúmenes.
 * 
 * @param texto - Texto a analizar
 * @returns true si hay indicios que merecen análisis detallado
 */
export function tieneIndiciosED50(texto: string): boolean {
  if (!texto || texto.length === 0) return false;
  
  const textoNormalizado = texto.toUpperCase();
  
  // Búsqueda rápida de strings clave
  const patronesRapidos = [
    'ED50', 'ED-50', 'ED 50',
    '23029', '23030', '23031',
    'HAYFORD', 'DATUM EUROPEO',
    'EUROPEAN DATUM',
  ];
  
  return patronesRapidos.some(p => textoNormalizado.includes(p));
}

/**
 * Detecta referencias explícitas a ED50 en texto
 */
function detectarReferenciasExplicitas(texto: string): IndicadorED50[] {
  const indicadores: IndicadorED50[] = [];
  
  for (const patron of PATRONES_ED50_EXPLICITO) {
    const regex = new RegExp(patron.source, patron.flags);
    let match: RegExpExecArray | null;
    
    while ((match = regex.exec(texto)) !== null) {
      indicadores.push({
        tipo: 'REFERENCIA_EXPLICITA',
        valor: match[0],
        contexto: extraerContexto(texto, match.index),
        linea: calcularLinea(texto, match.index),
        confianza: 'ALTA',
      });
    }
  }
  
  return indicadores;
}

/**
 * Detecta códigos EPSG de ED50
 */
function detectarCodigosEPSG(texto: string): IndicadorED50[] {
  const indicadores: IndicadorED50[] = [];
  const codigosED50 = ['23029', '23030', '23031'];
  
  // Primero buscar con prefijo EPSG (alta confianza)
  for (const patron of PATRONES_EPSG_ED50.slice(0, 3)) {
    const regex = new RegExp(patron.source, patron.flags);
    let match: RegExpExecArray | null;
    
    while ((match = regex.exec(texto)) !== null) {
      const codigo = codigosED50.find(c => match![0].includes(c)) || match[0];
      indicadores.push({
        tipo: 'CODIGO_EPSG',
        valor: codigo,
        contexto: extraerContexto(texto, match.index),
        linea: calcularLinea(texto, match.index),
        confianza: 'ALTA',
      });
    }
  }
  
  // Buscar códigos sueltos solo si hay contexto de coordenadas
  const tieneContextoCoordenadas = /\b(UTM|coordenada|proyecci[oó]n|datum|EPSG|CRS|SRS)\b/i.test(texto);
  
  if (tieneContextoCoordenadas) {
    for (const codigo of codigosED50) {
      const regex = new RegExp(`\\b${codigo}\\b`, 'g');
      let match: RegExpExecArray | null;
      
      while ((match = regex.exec(texto)) !== null) {
        // Evitar duplicados con detección EPSG explícita
        const yaDetectado = indicadores.some(i => 
          i.tipo === 'CODIGO_EPSG' && 
          Math.abs((i.linea || 0) - calcularLinea(texto, match!.index)) < 2
        );
        
        if (!yaDetectado) {
          indicadores.push({
            tipo: 'CODIGO_EPSG',
            valor: codigo,
            contexto: extraerContexto(texto, match.index),
            linea: calcularLinea(texto, match.index),
            confianza: 'MEDIA', // Menor confianza sin prefijo EPSG
          });
        }
      }
    }
  }
  
  return indicadores;
}

/**
 * Detecta software legacy que usaba ED50 por defecto
 */
function detectarSoftwareLegacy(texto: string): IndicadorED50[] {
  const indicadores: IndicadorED50[] = [];
  
  for (const patron of SOFTWARE_ED50_LEGACY) {
    const regex = new RegExp(patron.source, patron.flags);
    let match: RegExpExecArray | null;
    
    while ((match = regex.exec(texto)) !== null) {
      indicadores.push({
        tipo: 'METADATO_APLICACION',
        valor: match[0],
        contexto: extraerContexto(texto, match.index),
        linea: calcularLinea(texto, match.index),
        confianza: 'MEDIA',
      });
    }
  }
  
  return indicadores;
}

/**
 * Analiza fecha del documento para determinar si es pre-transición
 * 
 * @param fecha - Fecha como Date, string ISO, o timestamp
 * @returns Indicador si fecha es pre-2007, null si no aplica
 */
function analizarFechaDocumento(fecha?: Date | string | number): IndicadorED50 | null {
  if (!fecha) return null;
  
  let fechaObj: Date;
  
  if (fecha instanceof Date) {
    fechaObj = fecha;
  } else if (typeof fecha === 'string') {
    fechaObj = new Date(fecha);
  } else if (typeof fecha === 'number') {
    fechaObj = new Date(fecha);
  } else {
    return null;
  }
  
  // Validar fecha
  if (isNaN(fechaObj.getTime())) return null;
  
  const año = fechaObj.getFullYear();
  
  // Solo considerar fechas razonables (1980-2007)
  if (año < 1980 || año > AÑO_TRANSICION_ETRS89) return null;
  
  // Determinar confianza según antigüedad
  let confianza: NivelConfianzaED50;
  if (año < 2000) {
    confianza = 'ALTA';  // Muy probable ED50
  } else if (año < 2005) {
    confianza = 'MEDIA'; // Probable ED50
  } else {
    confianza = 'BAJA';  // Periodo de transición
  }
  
  return {
    tipo: 'FECHA_DOCUMENTO',
    valor: año.toString(),
    contexto: `Documento fechado en ${año}, anterior a la transición oficial a ETRS89 (2007)`,
    confianza,
  };
}

// =============================================================================
// FUNCIÓN PRINCIPAL
// =============================================================================

/**
 * Detecta posible uso de datum ED50 en documento.
 * 
 * Analiza texto y metadatos para identificar indicios de que las coordenadas
 * podrían estar en ED50 en lugar de ETRS89. No realiza transformación,
 * solo advierte al usuario.
 * 
 * @param texto - Contenido textual del documento (opcional)
 * @param fecha - Fecha de creación/modificación del documento (opcional)
 * @returns Resultado completo de la detección
 * 
 * @example
 * ```typescript
 * // Detección por texto
 * const resultado = detectarED50('Coordenadas UTM ED50 Huso 30');
 * if (resultado.posibleED50) {
 *   console.warn(resultado.warning);
 * }
 * 
 * // Detección por fecha
 * const resultado2 = detectarED50(undefined, new Date('2005-03-15'));
 * 
 * // Detección combinada
 * const resultado3 = detectarED50(textoDocumento, fechaCreacion);
 * ```
 */
export function detectarED50(
  texto?: string,
  fecha?: Date | string | number
): ResultadoDeteccionED50 {
  const indicadores: IndicadorED50[] = [];
  
  // Análisis de texto
  if (texto && texto.length > 0) {
    indicadores.push(...detectarReferenciasExplicitas(texto));
    indicadores.push(...detectarCodigosEPSG(texto));
    indicadores.push(...detectarSoftwareLegacy(texto));
  }
  
  // Análisis de fecha
  const indicadorFecha = analizarFechaDocumento(fecha);
  if (indicadorFecha) {
    indicadores.push(indicadorFecha);
  }
  
  // Calcular resultado
  const posibleED50 = indicadores.length > 0;
  const confianza = calcularConfianzaGlobal(indicadores);
  
  return {
    posibleED50,
    confianza,
    indicadoresEncontrados: indicadores,
    warning: posibleED50 ? generarWarning(indicadores, confianza) : null,
    recomendacion: posibleED50 ? generarRecomendacion(confianza) : null,
  };
}

// =============================================================================
// EXPORTS ADICIONALES PARA TESTING
// =============================================================================

export const _internals = {
  extraerContexto,
  calcularLinea,
  calcularConfianzaGlobal,
  generarWarning,
  generarRecomendacion,
  detectarReferenciasExplicitas,
  detectarCodigosEPSG,
  detectarSoftwareLegacy,
  analizarFechaDocumento,
  PATRONES_ED50_EXPLICITO,
  PATRONES_EPSG_ED50,
  SOFTWARE_ED50_LEGACY,
};
